import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { MdMenu } from 'react-icons/md';
import { HiOutlineDocumentReport } from 'react-icons/hi';
import { RiInboxUnarchiveLine } from 'react-icons/ri';
import { RiInboxArchiveLine } from 'react-icons/ri';
import { FiFolderPlus } from 'react-icons/fi';
import { AiOutlineStock } from 'react-icons/ai';
import { BsTools } from 'react-icons/bs';

import { useAppContext } from '../../contextApi/context';
import { checkPermission, checkReportPermissions } from '../../utils/permissions';

const Sidebar = () => {
    // ----- Const
    const { loggedUser } = useAppContext();

    // ----- State
    // Inicializa basado en el tamaño de pantalla
    const [expand, setExpand] = useState(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth >= 768; // Desktop (≥768px): TRUE, Móvil: FALSE
        }
        return true;
    });

    // Para forzar re-renderizado en cambios de pantalla
    const [windowWidth, setWindowWidth] = useState(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth;
        }
        return 1200;
    });

    // ----- Actions
    const handleToggle = () => {
        const newExpand = !expand;
        setExpand(newExpand);

        // Al cerrar en móvil: se cierran los dropdowns
        if (!newExpand && isMobile) {
            setTimeout(closeAllDropdowns, 50);
        }
    };

    // Cierra todos los dropdowns de Bootstrap - Necesario porque al cerrar el sidebar en móviles, los dropdowns quedan abiertos y salen en la pantalla
    const closeAllDropdowns = () => {
        try {
            const dropdownIds = ['reports', 'cruds'];

            dropdownIds.forEach((id) => {
                const dropdownElement = document.getElementById(id);
                if (dropdownElement) {
                    // Método directo: remover clases de Bootstrap y forzar estado cerrado
                    dropdownElement.classList.remove('show', 'collapsing');
                    dropdownElement.classList.add('collapse');
                    dropdownElement.style.height = '';
                    dropdownElement.style.display = '';

                    // También actualizar el aria-expanded de los botones asociados
                    const toggleButton = document.querySelector(`[data-bs-target="#${id}"]`);
                    if (toggleButton) {
                        toggleButton.classList.add('collapsed');
                        toggleButton.setAttribute('aria-expanded', 'false');
                    }
                }
            });
        } catch (error) {
            console.warn('Error closing dropdowns:', error);
        }
    };

    // Función para manejar clicks en los enlaces
    const handleLinkClick = () => {
        // Cerrar menu y dropdowns en móvil
        if (isMobile) {
            setExpand(false);
            setTimeout(closeAllDropdowns, 50);
        }
    };

    // Maneja cambios de tamaño de pantalla
    useEffect(() => {
        const handleResize = () => {
            const newWidth = window.innerWidth;
            setWindowWidth(newWidth);

            const isMobile = newWidth < 768;
            // De móvil a desktop: expandir
            if (!isMobile && !expand) {
                setExpand(true);
            }
            // De desktop a móvil: contraer y cerrar dropdowns
            else if (isMobile && expand) {
                setExpand(false);
                setTimeout(closeAllDropdowns, 50);
            }
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }
    }, [expand]);

    // Helper actualizado que usa el state
    const isMobile = windowWidth < 768;

    // ----- Render
    return (
        <>
            {/* Overlay para cerrar sidebar en móviles */}
            {isMobile && expand && <div className='sidebar-overlay' onClick={handleToggle} />}

            {/* Icono flotante - Solo para móviles cuando el sidebar está cerrado */}
            {isMobile && !expand && (
                <button className='floating-hamburger' onClick={handleToggle} type='button'>
                    <MdMenu />
                </button>
            )}

            {/* Sidebar principal - En móviles siempre se renderiza pero se posiciona fuera de pantalla cuando está cerrado */}
            <nav id='sidebar' className={`${expand ? 'expand' : ''} ${isMobile ? 'mobile-sidebar' : ''}`}>
                <div className='d-flex'>
                    <button className='toggle-btn' onClick={handleToggle} type='button'>
                        <MdMenu />
                    </button>
                    <div className='sidebar-logo'>
                        <span>Menu</span>
                    </div>
                </div>
                <ul className='sidebar-nav'>
                    {checkPermission(loggedUser.permissions, 'view_stock') && (
                        <li className='sidebar-item'>
                            <Link to='/inventario' className='sidebar-link' onClick={handleLinkClick}>
                                <AiOutlineStock />
                                <span>Inventario</span>
                            </Link>
                        </li>
                    )}
                    {checkPermission(loggedUser.permissions, 'view_stock_receipt') && (
                        <li className='sidebar-item'>
                            <Link to='/compras' className='sidebar-link' onClick={handleLinkClick}>
                                <RiInboxArchiveLine />
                                <span>Compras</span>
                            </Link>
                        </li>
                    )}
                    {checkPermission(loggedUser.permissions, 'view_stock_issue') && (
                        <li className='sidebar-item'>
                            <Link to='/despacho' className='sidebar-link' onClick={handleLinkClick}>
                                <RiInboxUnarchiveLine />
                                <span>Despacho</span>
                            </Link>
                        </li>
                    )}
                    {checkPermission(loggedUser.permissions, ['view_work_orders', 'add_own_work_orders']) && (
                        <li className='sidebar-item'>
                            <Link to='/ordenes' className='sidebar-link' onClick={handleLinkClick}>
                                <BsTools />
                                <span>Órdenes de Trabajo</span>
                            </Link>
                        </li>
                    )}

                    {checkReportPermissions(loggedUser.permissions) && (
                        <li className='sidebar-item'>
                            <a
                                href='/'
                                className='sidebar-link collapsed has-dropdown'
                                data-bs-toggle='collapse'
                                data-bs-target='#reports'
                                aria-expanded='false'
                                aria-controls='reports'
                            >
                                <HiOutlineDocumentReport />
                                <span>Reportes</span>
                            </a>
                            <ul id='reports' className='sidebar-dropdown list-unstyled collapse' data-bs-parent='#sidebar'>
                                {checkPermission(loggedUser.permissions, 'view_financial_report') && (
                                    <li className='sidebar-item'>
                                        <Link to='/reporte-financiero' className='sidebar-link' onClick={handleLinkClick}>
                                            Reporte Financiero
                                        </Link>
                                    </li>
                                )}
                                {checkPermission(loggedUser.permissions, 'view_monthly_movements_report') && (
                                    <li className='sidebar-item'>
                                        <Link to='/reporte-consolidado' className='sidebar-link' onClick={handleLinkClick}>
                                            Reporte Consolidado por Mes
                                        </Link>
                                    </li>
                                )}
                                {checkPermission(loggedUser.permissions, 'view_stock_report') && (
                                    <li className='sidebar-item'>
                                        <Link to='/reporte-existencias' className='sidebar-link' onClick={handleLinkClick}>
                                            Existencias en Tiempo Real
                                        </Link>
                                    </li>
                                )}
                                {checkPermission(loggedUser.permissions, 'view_movements_report') && (
                                    <li className='sidebar-item'>
                                        <Link to='/reporte-ajustes' className='sidebar-link' onClick={handleLinkClick}>
                                            Ajustes de Inventario
                                        </Link>
                                    </li>
                                )}
                                {checkPermission(loggedUser.permissions, 'view_minimums_report') && (
                                    <li className='sidebar-item'>
                                        <Link to='/reporte-minimos' className='sidebar-link' onClick={handleLinkClick}>
                                            Inventario en Riesgo
                                        </Link>
                                    </li>
                                )}
                                {checkPermission(loggedUser.permissions, 'view_provider_receipt_report') && (
                                    <li className='sidebar-item'>
                                        <Link to='/reporte-compras-proveedor' className='sidebar-link' onClick={handleLinkClick}>
                                            Compras por Proveedor
                                        </Link>
                                    </li>
                                )}
                                {checkPermission(loggedUser.permissions, 'view_vehicle_issue_report') && (
                                    <li className='sidebar-item'>
                                        <Link to='/reporte-despachos-placa' className='sidebar-link' onClick={handleLinkClick}>
                                            Despachos por Placa
                                        </Link>
                                    </li>
                                )}
                                {checkPermission(loggedUser.permissions, 'view_article_receipt_report') && (
                                    <li className='sidebar-item'>
                                        <Link to='/reporte-compras-articulo' className='sidebar-link' onClick={handleLinkClick}>
                                            Compras por Artículo
                                        </Link>
                                    </li>
                                )}
                                {checkPermission(loggedUser.permissions, 'view_article_issue_report') && (
                                    <li className='sidebar-item'>
                                        <Link to='/reporte-despachos-articulo' className='sidebar-link' onClick={handleLinkClick}>
                                            Despachos por Artículo
                                        </Link>
                                    </li>
                                )}
                                {checkPermission(loggedUser.permissions, 'view_work_order_reports') && (
                                    <li className='sidebar-item'>
                                        <Link to='/reporte-ordenes-de-trabajo' className='sidebar-link' onClick={handleLinkClick}>
                                            Órdenes de Trabajo
                                        </Link>
                                    </li>
                                )}
                                {checkPermission(loggedUser.permissions, 'view_work_order_reports') && (
                                    <li className='sidebar-item'>
                                        <Link to='/reporte-servicios-internos' className='sidebar-link' onClick={handleLinkClick}>
                                            Servicios Internos
                                        </Link>
                                    </li>
                                )}
                                {checkPermission(loggedUser.permissions, 'view_work_order_reports') && (
                                    <li className='sidebar-item'>
                                        <Link to='/reporte-servicios-externos' className='sidebar-link' onClick={handleLinkClick}>
                                            Servicios Externos
                                        </Link>
                                    </li>
                                )}
                                {checkPermission(loggedUser.permissions, 'view_work_order_reports') && (
                                    <li className='sidebar-item'>
                                        <Link to='/reporte-valor-despachos-por-ot' className='sidebar-link' onClick={handleLinkClick}>
                                            Despachos por Orden de Trabajo
                                        </Link>
                                    </li>
                                )}
                            </ul>
                        </li>
                    )}
                    <li className='sidebar-item'>
                        <a
                            href='/'
                            className='sidebar-link collapsed has-dropdown'
                            data-bs-toggle='collapse'
                            data-bs-target='#cruds'
                            aria-expanded='false'
                            aria-controls='cruds'
                        >
                            <FiFolderPlus />
                            <span>Mantenimientos</span>
                        </a>
                        <ul id='cruds' className='sidebar-dropdown list-unstyled collapse' data-bs-parent='#sidebar'>
                            {checkPermission(loggedUser.permissions, 'view_articles') && (
                                <li className='sidebar-item'>
                                    <Link to='/articulos' className='sidebar-link' onClick={handleLinkClick}>
                                        Artículos
                                    </Link>
                                </li>
                            )}
                            {checkPermission(loggedUser.permissions, 'view_clients') && (
                                <li className='sidebar-item'>
                                    <Link to='/clientes' className='sidebar-link' onClick={handleLinkClick}>
                                        Clientes
                                    </Link>
                                </li>
                            )}
                            {checkPermission(loggedUser.permissions, 'view_tags') && (
                                <li className='sidebar-item'>
                                    <Link to='/etiquetas' className='sidebar-link' onClick={handleLinkClick}>
                                        Etiquetas
                                    </Link>
                                </li>
                            )}
                            {checkPermission(loggedUser.permissions, 'view_materials') && (
                                <li className='sidebar-item'>
                                    <Link to='/materiales' className='sidebar-link' onClick={handleLinkClick}>
                                        Materiales
                                    </Link>
                                </li>
                            )}
                            {checkPermission(loggedUser.permissions, 'view_providers') && (
                                <li className='sidebar-item'>
                                    <Link to='/proveedores' className='sidebar-link' onClick={handleLinkClick}>
                                        Proveedores
                                    </Link>
                                </li>
                            )}
                            {checkPermission(loggedUser.permissions, 'view_roles') && (
                                <li className='sidebar-item'>
                                    <Link to='/roles' className='sidebar-link' onClick={handleLinkClick}>
                                        Roles
                                    </Link>
                                </li>
                            )}
                            {checkPermission(loggedUser.permissions, 'view_locations') && (
                                <li className='sidebar-item'>
                                    <Link to='/ubicaciones' className='sidebar-link' onClick={handleLinkClick}>
                                        Ubicaciones
                                    </Link>
                                </li>
                            )}
                            {checkPermission(loggedUser.permissions, 'view_users') && (
                                <li className='sidebar-item'>
                                    <Link to='/usuarios' className='sidebar-link' onClick={handleLinkClick}>
                                        Usuarios
                                    </Link>
                                </li>
                            )}
                            {checkPermission(loggedUser.permissions, 'view_vehicles') && (
                                <li className='sidebar-item'>
                                    <Link to='/vehiculos' className='sidebar-link' onClick={handleLinkClick}>
                                        Vehículos
                                    </Link>
                                </li>
                            )}
                            {checkPermission(loggedUser.permissions, 'manage_exchange') && (
                                <li className='sidebar-item'>
                                    <Link to='/tipo-cambio' className='sidebar-link' onClick={handleLinkClick}>
                                        Tipo de Cambio
                                    </Link>
                                </li>
                            )}
                            {checkPermission(loggedUser.permissions, 'view_issue_types') && (
                                <li className='sidebar-item'>
                                    <Link to='/tipos-despacho' className='sidebar-link' onClick={handleLinkClick}>
                                        Tipos de Despacho
                                    </Link>
                                </li>
                            )}
                        </ul>
                    </li>
                </ul>
            </nav>
        </>
    );
};

export default Sidebar;
